// Copyright 2023 Sorama B.V.
import { Point2D } from "datatypes/SoramaLocalApi";

export type PeakDetectionOptions = {
    startIndex?: number;
    endIndex?: number;
    zScoreThreshold?: number;
    applyVWindow?: boolean;
};

export class PeakDetection {
    /**
     * Checks if the value at the given index is a peak when its Z-Score is above the given threshold
     * @param data the input data
     * @param index the index to check if it's a peak
     * @param options the options for the peak detection.
     * Defaults: { startIndex: 0, endIndex: data.length, zScoreThreshold: 2 }
     * @returns true if the value at the given index is a peak
     */
    public static isPeak(data: Point2D[], index: number, options: PeakDetectionOptions = {}) {
        options.startIndex = options.startIndex ?? 0;
        options.endIndex = options.endIndex ?? data.length;
        options.zScoreThreshold = options.zScoreThreshold ?? 2;
        options.applyVWindow = options.applyVWindow ?? false;

        return (
            this.zScore(data, index, options.startIndex, options.endIndex, options.applyVWindow) >
            options.zScoreThreshold
        );
    }

    /**
     * Calculates the Z-Score of the y value in a Point2D dataset
     * @param data the input data
     * @param index the index to check if it's a peak
     * @param startIndex the start index of the data to use for the average and standard deviation calculation
     * @param startIndex the end index of the data to use for the average and standard deviation calculation
     * @returns the standard deviation of the value at the index (0 extact average, 1 standard deviation above average, -1 standard deviation below average)
     */
    public static zScore(
        data: Point2D[],
        index: number,
        startIndex = 0,
        endIndex: number = data.length,
        applyVWindow = false
    ) {
        const value = data[index].y;
        const average = this.average(data, startIndex, endIndex, applyVWindow);
        const standardDeviation = this.standardDeviation(data, startIndex, endIndex, average, applyVWindow);

        return (value - average) / standardDeviation;
    }

    /**
     * Calculates the Z-Score of the y value in a Point2D array, implemented using for loop to improve performance
     * @param data the input data
     * @param startIndex start index of the data to use for the average calculation
     * @param endIndex end index of the data to use for the average calculation
     * @returns the average of the data
     */
    public static average(data: Point2D[], startIndex = 0, endIndex: number = data.length, vWindow = false): number {
        let weight = 1;
        let sum = 0;

        for (let i = startIndex; i < endIndex; i++) {
            if (vWindow) weight = this.getVWindowWeight(i, startIndex, endIndex);
            sum += data[i].y * weight;
        }
        return sum / (endIndex - startIndex);
    }

    /**
     * Calculates the Z-Score of the y value in a Point2D array, implemented using for loop to improve performance
     * @param data the input data
     * @param startIndex the start index of the data to use for the standard deviation calculation
     * @param endIndex the end index of the data to use for the standard deviation calculation
     * @param avg the average of the data, if already calculated
     * @returns the standard deviation of the data within the given range
     */
    public static standardDeviation(
        data: Point2D[],
        startIndex = 0,
        endIndex: number = data.length,
        avg: number = this.average(data, startIndex, endIndex),
        vWindow = false
    ): number {
        let weight = 1;
        let squaredSum = 0;

        for (let i = startIndex; i < endIndex; i++) {
            if (vWindow) weight = this.getVWindowWeight(i, startIndex, endIndex);
            squaredSum += (data[i].y - avg) * (data[i].y - avg) * weight;
        }
        return Math.sqrt(squaredSum / (endIndex - startIndex));
    }

    // inverse triangle window [2, 1, 0, 1, 2]
    private static getVWindowWeight(index: number, start: number, end: number): number {
        const middle = (start + end - 1) / 2;
        return (4 * Math.abs(middle - index)) / (end - start);
    }
}
